// DLOM Model Selector — PSD §4.2 (F4.2). Builder Step 6.

import type { DlomModel, DiscountInputs, AllocationAssumptions } from '@backsolve/shared-types';
import { bsPutImpliedDlom, finnertyDlom, longstaffLookbackDlom } from '@backsolve/shared-logic/dlom';
import { minorityDiscountFromControlPremium } from '@backsolve/shared-logic/minorityDiscount';
import { Select, ExpressionInput, NumericDisplay } from 'calc-controls';
import { Tooltip } from '../common/Tooltip';

interface DlomModelSelectorProps {
  discounts: DiscountInputs;
  assumptions: AllocationAssumptions;
  onChange: (patch: Partial<DiscountInputs>) => void;
}

const MODELS: { value: DlomModel; label: string; description: string }[] = [
  { value: 'fixed', label: 'Fixed Percentage', description: 'User-specified DLOM percentage.' },
  { value: 'chaffe', label: 'Chaffe (Protective Put)', description: 'ATM put price / spot via Black-Scholes.' },
  { value: 'bsPut', label: 'BS Put-Implied', description: 'Black-Scholes protective put model.' },
  { value: 'finnerty', label: 'Finnerty (2002)', description: 'Average-put approximation.' },
  { value: 'longstaff', label: 'Longstaff (1995)', description: 'Lookback-option upper bound.' },
];

export function DlomModelSelector({ discounts, assumptions, onChange }: DlomModelSelectorProps) {
  const computeModelPct = (model: DlomModel): number => {
    switch (model) {
      case 'chaffe':
      case 'bsPut':
        return bsPutImpliedDlom(assumptions);
      case 'finnerty':
        return finnertyDlom(assumptions);
      case 'longstaff':
        return longstaffLookbackDlom(assumptions);
      default:
        return discounts.dlomPercentage;
    }
  };

  const impliedPct = computeModelPct(discounts.dlomModel);
  const minorityFromPremium = minorityDiscountFromControlPremium(0.25);

  return (
    <div className="dlom-model-selector">
      <div className="form-group">
        <Select
          label="DLOM Model"
          tooltip="Discount for Lack of Marketability. Fixed = enter a percentage manually. The other models derive it from a Black-Scholes / Finnerty / Longstaff protective-put formulation. Clamped to [0%, 60%]."
          options={MODELS.map((m) => ({ value: m.value, label: m.label }))}
          value={discounts.dlomModel}
          onChange={(v) => onChange({ dlomModel: v as DlomModel })}
        />
      </div>

      <p className="field-hint">
        {MODELS.find((m) => m.value === discounts.dlomModel)?.description}
      </p>

      {discounts.dlomModel === 'fixed' && (
        <div className="form-group">
          <ExpressionInput
            label={
              <>
                DLOM Percentage
                <Tooltip
                  label="Manual lack-of-marketability discount as a ratio in [0, 0.6]. Applied to common value after the minority discount."
                  ariaLabel="Help: DLOM Percentage"
                />
              </>
            }
            value={discounts.dlomPercentage}
            onChange={(v) => onChange({ dlomPercentage: v })}
            min={0}
            max={0.6}
            decimals={3}
            id="dlom-pct"
          />
        </div>
      )}

      <div className="dlom-benchmark">
        <h5>Model Benchmark</h5>
        <table className="data-table">
          <thead>
            <tr>
              <th>Model</th>
              <th>Implied DLOM</th>
            </tr>
          </thead>
          <tbody>
            {MODELS.filter((m) => m.value !== 'fixed').map((m) => (
              <tr key={m.value} className={m.value === discounts.dlomModel ? 'row-selected' : ''}>
                <td>{m.label}</td>
                <td>
                  <NumericDisplay
                    inline
                    value={computeModelPct(m.value) * 100}
                    unit="%"
                    decimals={2}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="field-hint">
          Selected model implies{' '}
          <NumericDisplay inline value={impliedPct * 100} unit="%" decimals={2} /> DLOM.
        </div>
        {(() => {
          const modelPcts = MODELS.filter((m) => m.value !== 'fixed').map((m) => computeModelPct(m.value));
          const allPcts = discounts.dlomModel === 'fixed' ? [...modelPcts, discounts.dlomPercentage] : modelPcts;
          const spread = allPcts.length > 0 ? Math.max(...allPcts) - Math.min(...allPcts) : 0;
          return (
            <>
              <p className="field-hint">
                Model spread: <strong>{(spread * 100).toFixed(1)}pp</strong>
              </p>
              {spread > 0.1 && (
                <p className="badge badge-warning mt-2">
                  DLOM is highly model-sensitive — document your choice.
                </p>
              )}
            </>
          );
        })()}
      </div>

      <div className="form-group">
        <ExpressionInput
          label={
            <>
              Minority Discount
              <Tooltip
                label="Lack-of-control discount applied before DLOM. Derived as 1 − 1/(1 + controlPremium). Suggested value below assumes a 25% control premium."
                ariaLabel="Help: Minority Discount"
              />
            </>
          }
          value={discounts.minorityDiscount}
          onChange={(v) => onChange({ minorityDiscount: v })}
          min={0}
          max={0.5}
          decimals={3}
          id="minority-disc"
        />
        <div className="field-hint">
          Suggested from 25% control premium:{' '}
          <NumericDisplay inline value={minorityFromPremium * 100} unit="%" decimals={1} />
        </div>
      </div>
    </div>
  );
}
